<template>
  <v-container>

    <error-dialog :status="loadStatus" />
    <error-dialog :status="handleUploadStatus" />

    <dialog-scroll-lock v-if="handleUploadStatus.pending" persistent :value="true">
      <snackbar persistent value>
        <div class="mb-3">Uploading file: {{uploadPct}}%</div>
        <v-progress-linear color="white" :value="uploadPct" />
        <div />
      </snackbar>
    </dialog-scroll-lock>

    <h1 class="mt-6 text-h4 text-sm-h3 header-font">Secure file transfer</h1>
    <div class="mt-3 mb-12">Store files and transfer them to Ellacard</div>

    <div v-if="loadAccountStatus.pending">
      <v-progress-circular indeterminate />
    </div>

    <div v-else-if="!account || !account.files" class="warning--text">
      <v-icon class="mr-2" color="warning" v-html="mdiAlert" />You don't have permission to access this page
    </div>

    <template v-else>
      <input v-show="false" ref="upload-input" type="file" />
      <div class="d-flex justify-sm-end pa-3">
        <v-btn small color="primary" :loading="handleUploadStatus.pending" @click="upload">Upload…</v-btn>
      </div>
      <v-data-table
        sort-by="created_at"
        hide-default-footer
        :headers="[
          { value: 'download', sortable: false },
          { text: 'Name', value: 'name' },
          { text: 'Created', value: 'created_at' },
          { value: 'delete', sortable: false }
        ]"
        :loading="loadStatus.pending"
        :disable-sort="loadStatus.pending"
        :disable-pagination="loadStatus.pending"
        :items="files"
      >
        <template v-slot:item.download="slot">
          <!-- We don't want regular people to be able to use this system for arbitrary file sharing, but it's useful for those in the know -->
          <v-btn v-if="global.profile?.admin" icon @click="copyToClipboard(getURL(slot.item.key))">
            <v-icon v-html="mdiClipboardArrowUp" />
          </v-btn>
          <v-btn icon :loading="(track == slot.item) && downloadStatus.pending" @click="download(slot.item)">
            <v-icon v-html="mdiDownload" />
          </v-btn>
        </template>
        <template v-slot:item.created_at="slot">
          {{slot.value | moment('short-date')}}
        </template>
        <template v-slot:item.delete="slot">
          <v-btn icon :loading="(track == slot.item) && deleteFileStatus.pending" @click="deleteFile(slot.item)">
            <v-icon v-html="mdiDelete" />
          </v-btn>
        </template>
      </v-data-table>
    </template>
  </v-container>
</template>

<script>
import ErrorDialog from '@/components/ErrorDialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { mdiDelete, mdiDownload, mdiAlert, mdiClipboardArrowUp } from '@mdi/js';

export default {
  name: 'file-transfer',

  routerViewMeta: {
    title: 'Ellacard file transfer',
    description: 'Secure large file transfer to / from Ellacard'
  },

  components: {
    ErrorDialog,
    Snackbar
  },

  data() {
    return {
      account: null,
      files: [],
      track: null,
      uploadPct: 50,
      loadAccountStatus: this.$track('loadAccount'),
      loadStatus: this.$track('load'),
      handleUploadStatus: this.$track('handleUpload'),
      downloadStatus: this.$track('download'),
      deleteFileStatus: this.$track('deleteFile')
    };
  },

  created() {
    Object.assign(this, { mdiDelete, mdiDownload, mdiAlert, mdiClipboardArrowUp });
  },

  methods: {
    async loadAccount() {
      this.account = await this.$call('get-account');
    },

    upload() {
      const el = this.$refs['upload-input'];
      const onChange = e => {
        if (e.target.files[0])
          this.handleUpload(e.target.files[0]);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.onchange = onChange;
      el.click();
    },

    async handleUpload(file) {
      this.uploadPct = 0;
      const name = file.name || 'Unknown';
      const type = file.type || 'application/octet-stream';
      const { key, url } = await this.$call('create-file-get-presigned-url', { type });
      await new Promise(
        (resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', url, true);
          xhr.upload.onprogress = e => {
            if (e.lengthComputable)
              this.uploadPct = Math.round(100 * event.loaded / event.total);
          };
          xhr.onload = () => (xhr.status == 200) ? resolve() : reject();
          xhr.onerror = reject;
          xhr.setRequestHeader('Content-Type', type);
          xhr.send(file);
        }
      );

      // const reader = file.stream().getReader();
      // let sent = 0;
      // const body = new ReadableStream({
      //   async start(controller) {
      //     while (true) {
      //       const { done, value } = await reader.read();
      //       if (done) break;
      //       sent += value.length;
      //       this.uploadPct = Math.round(100 * sent / file.size);
      //       controller.enqueue(value);
      //     }
      //     controller.close();
      //   }
      // });
      // const r = await fetch(url, { method: 'PUT', body, duplex: 'half', headers: { 'Content-Type': file.type } });
      // if (!r.ok)
      //   throw new Error();

      await this.$call('create-file-with-presigned-url', { name, key });
      this.$notify('File uploaded successfully');
      this.load();
    },

    getURL(key) {
      return 'https://ellacard-public.s3.amazonaws.com/' + key;
    },

    async download(file) {
      // We fetch the file in the front-end because browsers will not respect the 'download' attribute for CORS requests
      this.track = file;
      const s3URL = this.getURL(file.key);
      const blobURL = URL.createObjectURL(await fetch(s3URL).then(r => r.blob()));
      const link = document.createElement('a');
      link.href = blobURL;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    async deleteFile(file) {
      this.track = file;
      await this.$call('delete-file', { file_id: file.id });
      this.load();
    },

    async load() {
      this.files = [];
      if (!this.isSignedIn)
        return;
      const response = await this.$call('list-files');
      this.files = response.map(([ id, file ]) => ({ id, ...file}));
    }
  },

  watch: {
    globalUserId: {
      immediate: true,
      handler(v) {
        this.account = null;
        v && this.loadAccount();
      }
    },

    'account.files'(v) {
      if (v)
        this.load();
    }
  }
}
</script>
